/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  CampaignReadyModal,
  DiscountModal,
  SinglePurchaseModal,
  ExitFeedbackModal,
  PaymentSuccessModal,
} from '../../../../components/CampaignUpsellModals'
import {
  UPSELL_FLOWS,
  useUpsellCatalog,
  useUpgradePlansTab,
  useUpsellEventMap,
  useUpsellFlowState,
  useUpsellCheckout,
  useUpsellSuccessClose,
  trackUpsellExitFeedback,
  resolvePlanCheckoutPayload,
  resolvePackageCheckoutPayload,
} from '../UpsellFlows'
import {
  UPSELL_FEATURED_PLAN_TIER,
  filterStaticPlanOptions,
  UPSELL_CAMPAIGN_PACKAGE_CODE,
  UPSELL_DOWNLOAD_PACKAGE_CODE,
  buildCouponPackageOffer,
  buildDownloadOffer,
  buildMarketComparisonPrice,
  buildPlanMarketComparisonPrice,
  PRIMEIRA20_COUPON_CODE,
} from '../UpsellFlows/upsellCatalog'
import {
  DOWNLOAD_UPSELL_STEPS,
  PLAN_FEATURED,
  PLAN_OPTIONS,
} from './constants'
import styles from './styles.module.css'

export const CampaignUpsellFlow = forwardRef(function CampaignUpsellFlow(
  {
    enabled = false,
    campaignId = null,
    showPreviewButton = false,
    onOpen = null,
  },
  ref,
) {
  const { t } = useTranslation()
  const catalog = useUpsellCatalog({ enabled })
  const {
    openSubscriptionCheckout,
    openAddonCheckout,
  } = useUpsellCheckout()
  const {
    activeTab,
    setActiveTab,
    closeFlow,
    openFlow,
    isStep,
    goToStep,
    openSuccessStep,
    currentStep,
    isSuccessStepActive,
  } = useUpsellFlowState({
    enabled,
    initialStep: DOWNLOAD_UPSELL_STEPS.READY,
  })
  const upsellTabs = useUpgradePlansTab(
    catalog,
    activeTab,
    setActiveTab,
    [
      { id: 'packages', label: t('upsell.tabs.packages') },
      { id: 'plans', label: t('upsell.tabs.plans') },
    ],
  )

  const exitFeedbackOptions = useMemo(
    () => [
      { id: 'price', label: t('upsell.exit_feedback.price') },
      { id: 'trial', label: t('upsell.exit_feedback.trial') },
      { id: 'quality', label: t('upsell.exit_feedback.quality') },
      { id: 'exploring', label: t('upsell.exit_feedback.exploring') },
    ],
    [t],
  )

  useUpsellEventMap({
    flow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
    campaignId,
    currentStep,
    activeTab,
  })

  const featuredPlan = catalog.featuredUpgradePlan ?? catalog.featuredPlan

  const featuredPlanOffer = useMemo(() => {
    const base = catalog.buildFeaturedPlanOffer()
    if (!base) return null

    const planName = base.name ?? PLAN_FEATURED.name
    const artsPerMonth = base.artsPerMonth ?? PLAN_FEATURED.artsPerMonth

    return {
      ...base,
      label: t('upsell.campaign_download.plan_featured_label', {
        plan: planName,
        count: artsPerMonth,
      }),
      description: t('upsell.plan_description'),
    }
  }, [catalog, t])

  const featuredUpgradePlan = catalog.featuredUpgradePlan

  const featuredPackageOffer = useMemo(
    () =>
      catalog.buildFeaturedPackageOffer(catalog.campaignPackage, {
        description: t('upsell.campaign_download.package_description'),
        subdescription: t('upsell.package_subdescription'),
      }),
    [catalog, t],
  )

  const discountOffer = useMemo(
    () =>
      buildCouponPackageOffer(catalog.campaignPackage, {
        couponCode: PRIMEIRA20_COUPON_CODE,
      }),
    [catalog.campaignPackage],
  )

  const downloadOffer = useMemo(
    () => buildDownloadOffer(catalog.downloadPackage),
    [catalog.downloadPackage],
  )

  const packageMarketPrice = useMemo(
    () => buildMarketComparisonPrice(catalog.downloadPackage, { multiplier: 22 }),
    [catalog.downloadPackage],
  )

  const planMarketPrice = useMemo(
    () => buildPlanMarketComparisonPrice(featuredPlan),
    [featuredPlan],
  )

  const packageOptions = useMemo(
    () => catalog.getPackageOptions([UPSELL_CAMPAIGN_PACKAGE_CODE]),
    [catalog],
  )

  const planOptions = useMemo(() => {
    const featuredTier =
      catalog.featuredUpgradePlan?.tier ?? UPSELL_FEATURED_PLAN_TIER

    if (!catalog.dbPlans.length) {
      return filterStaticPlanOptions(
        PLAN_OPTIONS,
        catalog.currentPlanTier,
        featuredTier,
      )
    }

    return catalog.getUpgradePlanOptions()
  }, [catalog])

  const handleOpenPackageCheckout = useCallback((option) => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        option,
        catalog.campaignPackage,
        UPSELL_CAMPAIGN_PACKAGE_CODE,
      ),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS,
      onOpen: closeFlow,
    })
  }, [campaignId, catalog.campaignPackage, closeFlow, openAddonCheckout])

  const handleOpenPlanCheckout = useCallback((option) => {
    openSubscriptionCheckout({
      ...resolvePlanCheckoutPayload(option, featuredUpgradePlan),
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS_PLAN,
      onOpen: closeFlow,
    })
  }, [campaignId, closeFlow, featuredUpgradePlan, openSubscriptionCheckout])

  const handleOpenFeaturedPackageCheckout = useCallback(() => {
    handleOpenPackageCheckout(catalog.campaignPackage)
  }, [catalog.campaignPackage, handleOpenPackageCheckout])

  const handleOpenDiscountPackageCheckout = useCallback(() => {
    openAddonCheckout({
      ...resolvePackageCheckoutPayload(
        null,
        catalog.campaignPackage,
        UPSELL_CAMPAIGN_PACKAGE_CODE,
      ),
      price: discountOffer?.priceValue,
      promotionCode: discountOffer?.couponCode ?? PRIMEIRA20_COUPON_CODE,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS_DISCOUNT,
      onOpen: closeFlow,
    })
  }, [campaignId, catalog.campaignPackage, closeFlow, discountOffer, openAddonCheckout])

  const handleOpenSingleDownloadCheckout = useCallback(() => {
    if (!campaignId || !downloadOffer) return

    openAddonCheckout({
      packageCode: UPSELL_DOWNLOAD_PACKAGE_CODE,
      name: downloadOffer.label,
      price: downloadOffer.priceValue,
      campaignArtGenerationId: campaignId,
      upsellFlow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
      upsellSuccessStep: DOWNLOAD_UPSELL_STEPS.SUCCESS_SINGLE,
      onOpen: closeFlow,
    })
  }, [campaignId, closeFlow, downloadOffer, openAddonCheckout])

  const handleOpenFeaturedPlanCheckout = useCallback(() => {
    handleOpenPlanCheckout(featuredUpgradePlan)
  }, [featuredUpgradePlan, handleOpenPlanCheckout])

  const closeSuccessFlow = useUpsellSuccessClose(closeFlow, isSuccessStepActive)

  const withClose = (action) => ({
    ...action,
    onClick: closeSuccessFlow,
  })

  useImperativeHandle(ref, () => ({
    open: () => {
      const result = openFlow()
      if (result) {
        onOpen?.()
      }
      return result
    },
    close: closeFlow,
    openSuccessStep,
  }), [openFlow, closeFlow, openSuccessStep, onOpen])

  if (!enabled && !isSuccessStepActive) {
    return null
  }

  const packagesContent = {
    title: t('upsell.campaign_download.title'),
    subtitle: t('upsell.campaign_download.subtitle'),
    marketLabel: t('upsell.campaign_download.market_label_packages'),
    marketPrice: packageMarketPrice
      ? t('upsell.market_from_price', { price: packageMarketPrice })
      : null,
    platformLabel: t('upsell.arts_limit.platform_label'),
    platformPrice: featuredPackageOffer?.price,
    featuredOffer: featuredPackageOffer,
    otherOptionsTitle: t('upsell.campaign_download.other_packages_title'),
    otherOptions: packageOptions,
    onSelectOption: handleOpenPackageCheckout,
    primaryAction: {
      label: t('upsell.campaign_download.buy_and_download'),
      onClick: handleOpenFeaturedPackageCheckout,
    },
    tertiaryLink: catalog.hasSelectablePlans
      ? {
        label: t('upsell.campaign_download.plans_link'),
        onClick: () => setActiveTab('plans'),
      }
      : undefined,
    skipAction: {
      label: t('upsell.campaign_download.skip_download'),
      onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT),
    },
  }

  const plansContent = {
    title: t('upsell.campaign_download.title'),
    subtitle: t('upsell.campaign_download.subtitle'),
    marketLabel: t('upsell.arts_limit.market_label_plans'),
    marketPrice: planMarketPrice,
    platformLabel: featuredPlanOffer?.name
      ? t('upsell.plan_label', { plan: featuredPlanOffer.name })
      : t('upsell.plan_label', { plan: 'Pro' }),
    platformPrice: featuredPlanOffer?.price,
    featuredOffer: featuredPlanOffer,
    otherOptionsTitle: t('upsell.campaign_download.other_options_title'),
    otherOptions: planOptions,
    onSelectOption: handleOpenPlanCheckout,
    primaryAction: {
      label: featuredPlanOffer?.name
        ? t('upsell.campaign_download.subscribe_plan_download', {
          plan: featuredPlanOffer.name,
        })
        : t('upsell.campaign_download.subscribe_download'),
      onClick: handleOpenFeaturedPlanCheckout,
    },
    tertiaryLink: {
      label: t('upsell.campaign_download.packages_link'),
      onClick: () => setActiveTab('packages'),
    },
    skipAction: {
      label: t('upsell.campaign_download.skip_download_short'),
      onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT),
    },
  }

  return (
    <>
      {showPreviewButton && (
        <button
          type="button"
          className={styles.upsellFlowPreviewButton}
          onClick={openFlow}
        >
          {t('upsell.campaign_download.preview_button')}
        </button>
      )}

      <CampaignReadyModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.READY)}
        onClose={() => goToStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT)}
        tabs={upsellTabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        packages={packagesContent}
        plans={plansContent}
      />

      <DiscountModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.DISCOUNT)}
        onClose={() => goToStep(DOWNLOAD_UPSELL_STEPS.SINGLE)}
        offer={discountOffer}
        primaryAction={{
          label: t('upsell.campaign_download.use_discount'),
          onClick: handleOpenDiscountPackageCheckout,
        }}
        dismissAction={{
          label: t('upsell.campaign_download.decline_discount'),
          onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.SINGLE),
        }}
      />

      <SinglePurchaseModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SINGLE)}
        onClose={() => goToStep(DOWNLOAD_UPSELL_STEPS.EXIT)}
        offer={downloadOffer}
        primaryAction={{
          label: downloadOffer?.price
            ? t('upsell.download_offer.unlock_cta', { price: downloadOffer.price })
            : t('upsell.campaign_download.unlock_download'),
          onClick: handleOpenSingleDownloadCheckout,
        }}
        dismissAction={{
          label: t('upsell.arts_limit.exit_anyway'),
          onClick: () => goToStep(DOWNLOAD_UPSELL_STEPS.EXIT),
        }}
      />

      <ExitFeedbackModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.EXIT)}
        onClose={closeSuccessFlow}
        title={t('upsell.campaign_download.exit_title')}
        description={
          <Trans
            i18nKey="upsell.campaign_download.exit_description"
            components={{ strong: <strong /> }}
          />
        }
        feedback={{
          options: exitFeedbackOptions,
          question: t('upsell.campaign_download.exit_feedback_question'),
        }}
        onExitWithFeedback={(reason) =>
          trackUpsellExitFeedback({
            flow: UPSELL_FLOWS.CAMPAIGN_DOWNLOAD,
            campaignId,
            reasonId: reason.id,
            reasonLabel: reason.label,
          })
        }
        secondaryAction={withClose({
          label: t('upsell.arts_limit.back_to_platform'),
        })}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS)}
        onClose={closeSuccessFlow}
        title={t('upsell.campaign_download.success_default_title')}
        description={
          <Trans
            i18nKey="upsell.campaign_download.success_default_description"
            components={{ 1: <br /> }}
          />
        }
        primaryAction={withClose({
          label: t('upsell.campaign_download.back_to_campaign'),
        })}
        secondaryAction={withClose({
          label: t('upsell.campaign_download.create_next_campaign'),
        })}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS_DISCOUNT)}
        onClose={closeSuccessFlow}
        description={t('upsell.campaign_download.success_discount', {
          coupon: discountOffer?.couponCode ?? PRIMEIRA20_COUPON_CODE,
          price: discountOffer?.price,
          savings: discountOffer?.savings,
        })}
        primaryAction={withClose({
          label: t('upsell.campaign_download.back_to_campaign'),
        })}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS_SINGLE)}
        onClose={closeSuccessFlow}
        title={t('upsell.campaign_download.success_single_title')}
        description={t('upsell.campaign_download.success_single_description')}
        primaryAction={withClose({
          label: t('upsell.campaign_download.back_to_campaign'),
        })}
      />

      <PaymentSuccessModal
        isOpen={isStep(DOWNLOAD_UPSELL_STEPS.SUCCESS_PLAN)}
        onClose={closeSuccessFlow}
        title={t('upsell.campaign_download.success_plan_title')}
        description={t('upsell.campaign_download.success_plan_description', {
          plan: featuredPlanOffer?.name ?? PLAN_FEATURED.name,
          count: featuredPlanOffer?.artsPerMonth ?? PLAN_FEATURED.artsPerMonth,
        })}
        primaryAction={withClose({
          label: t('upsell.campaign_download.back_to_campaign'),
        })}
        secondaryAction={withClose({
          label: t('upsell.campaign_download.create_next_campaign'),
        })}
      />
    </>
  )
})

export default CampaignUpsellFlow
